import { Link } from "react-router-dom";
import { sourceShortLabel } from "./leadFilterLabels";
import { StatusPill } from "../../components/ui/StatusPill";
import { ContactActions } from "../../components/ui/ContactActions";
import { LastActivityCell } from "../activities/LastActivityCell";
import {
  formatRelative,
  formatFollowUpDate,
  isFollowUpOverdue,
} from "../../lib/format";
import { useMembersIndex } from "../team/useMembersIndex";
import type { LeadSummary } from "./api";

interface Props {
  leads: LeadSummary[];
}

export function LeadTable({ leads }: Props) {
  const { resolveName } = useMembersIndex();

  if (leads.length === 0) {
    return (
      <div className="empty-state">
        <h3>No leads match</h3>
        <p>Adjust the filters above, or wait for the next webhook delivery.</p>
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th>Source</th>
            <th>Campaign</th>
            <th>Phone / Email</th>
            <th>Assigned to</th>
            <th>Next follow-up</th>
            <th>Received</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((lead) => (
            <tr key={lead.id}>
              <td>
                <Link to={`/leads/${lead.id}`} className="row-link">
                  {lead.name ?? "(no name)"}
                </Link>
                {(lead.company || lead.rawFields?.subject) ? <div className="row-sub">{(lead.company || lead?.rawFields?.subject) as any}</div> : null}
              </td>
              <td>
                <StatusPill status={lead.status} />
                <LastActivityCell
                  leadId={lead.id}
                  type={lead.lastLoggedActivityType}
                  at={lead.lastLoggedActivityAt}
                />
              </td>
              <td><span className="badge">{sourceShortLabel(lead.source)}</span></td>
              <td>
                <div>{lead.sourceCampaignName ?? "—"}</div>
                {lead.sourceAdName ? <div className="row-sub">{lead.sourceAdName}</div> : null}
              </td>
              <td>
                <div className="contact-cell">
                  <div>
                    <div>{lead.phoneE164 ?? "—"}</div>
                    {lead.email ? <div className="row-sub">{lead.email}</div> : null}
                  </div>
                  <ContactActions
                    phone={lead.phoneE164}
                    email={lead.email}
                    variant="menu"
                    name={lead.name}
                  />
                </div>
              </td>
              <td>
                {lead.assignedTo ? (
                  <>
                    <div>{resolveName(lead.assignedTo)}</div>
                    {lead.assignedAt ? (
                      <div className="row-sub" title={lead.assignedAt}>
                        Assigned {formatFollowUpDate(lead.assignedAt)}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <span className="muted">Unassigned</span>
                )}
              </td>
              <td>
                {lead.nextFollowUpAt ? (
                  <span
                    className={
                      isFollowUpOverdue(lead.nextFollowUpAt)
                        ? "followup-due followup-overdue"
                        : "followup-due"
                    }
                    title={lead.nextFollowUpAt}
                  >
                    {formatFollowUpDate(lead.nextFollowUpAt)}
                  </span>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
              <td title={lead.createdAt}>{formatRelative(lead.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
